import React, { useState, useEffect } from 'react';
import CardSecondaryAction from '../../ui-component/cards/CardSecondaryAction';
import MainCard from '../../ui-component/cards/MainCard';
import { Checkbox, Grid } from '@mui/material';
import { gridSpacing } from '../../store/constant';
import configData from '../../config';
import axiosInstance from '../../axiosInstance';
import { IconDownload, IconUpload, IconListDetails } from '@tabler/icons';
import MaterialTable from '@material-table/core';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useSelector } from 'react-redux';

const columns = [
    { title: 'Org Id', field: 'org_id' },
    { title: 'Org Name', field: 'name' },
    { title: 'Industry', field: 'industry' },
    { title: 'Owner Name', field: 'org_owner' }
];

const memberCols = [
    { title: 'Name', field: 'memberName' },
    { title: 'Email', field: 'email' },
    { title: 'Phone', field: 'phone' },
    { title: 'Role', field: 'role' }
];

const icons = {
    IconDownload,
    IconUpload,
    IconListDetails
};

const ReportAllocation = () => {
    const [modal, setModal] = useState(false);
    const account = useSelector((state) => state.account);

    const [apiData, setAPIData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableRowData, setTableRowData] = useState();
    const [reportData, setReportData] = useState();
    const [updatedReportData, setUpdatedReportData] = useState();
    const [checkedItems, setCheckedItems] = useState({});
    const [parentChildPairs, setParentChildPairs] = useState([]);

    // Toggle Modal
    const toggle = () => {
        setModal(!modal);
        if (!modal) {
            setUpdatedReportData();  // Clear the updated report data only when closing
        }
    };

    // Actions for MaterialTable
    const actions = [
        {
            icon: icons.IconListDetails,
            tooltip: 'Manage Reports',
            onClick: async (event, rowData) => {
                toggle();
                setTableRowData(rowData);
            }
        }
    ];

    // Fetch organization data and reports
    useEffect(() => {
        const abortController = new AbortController();  // Create an AbortController instance for API requests

        const fetchOrgData = async () => {
            setLoading(true);
            try {
                let response;
                if (account.role === 'Owner') {
                    response = await axiosInstance.get(configData.API_SERVER + '/organization/get_org_with_org_owner', { signal: abortController.signal });
                } else if (account.role === 'Super Admin') {
                    response = await axiosInstance.get(configData.API_SERVER + `/organization/get_org_member_with_org_id?org_id=${account.org.id}&user=${account.user.role.role}`, { signal: abortController.signal });
                } else {
                    response = await axiosInstance.get(configData.API_SERVER + `/organization/get_org_member_with_org_id?org_id=${account.org.id}&user=${account.user.role}`, { signal: abortController.signal });
                }
                setAPIData(response?.data || []);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.log(error);
                }
            }
            setLoading(false);
        };

        const fetchReport = async () => {
            try {
                let res;
                if (account.role === 'Super Admin') {
                    res = await axiosInstance.get(configData.API_SERVER + `/user/allocated-reports?org_id=${account.org.id}`, { signal: abortController.signal });
                } else if (account.role === 'Owner') {
                    res = await axiosInstance.get(configData.API_SERVER + '/user/get_all_reports', { signal: abortController.signal });
                } else {
                    const user = await axiosInstance.get(configData.API_SERVER + `/user/get_user_with_email?email=${account.org_member.email}`, { signal: abortController.signal });
                    res = await axiosInstance.get(configData.API_SERVER + `/user/allocated-reports?user_id=${user?.data?.id}`, { signal: abortController.signal });
                }
                setReportData(res?.data);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.log(error);
                }
            }
        };

        fetchOrgData();
        fetchReport();

        return () => {
            // Cleanup: Abort the requests if the component unmounts
            abortController.abort();
        };
    }, [account]);

    // Fetch allocated reports based on selected table row
    useEffect(() => {
        const fetchAllocatedReports = async () => {
            if (tableRowData) {
                try {
                    let res;
                    if (account.role === 'Super Admin') {
                        res = await axiosInstance.get(configData.API_SERVER + `/user/get_allocated_reports_to_manage?user_id=${tableRowData.id}`);
                    } else if (account.role === 'Owner') {
                        res = await axiosInstance.get(configData.API_SERVER + `/user/get_allocated_reports_to_manage?org_id=${tableRowData.id}`);
                    }
                    const transformedData = transformData([...res.data]);
                    console.log("Setting updatedReportData:", transformedData); 
                    setUpdatedReportData(transformedData);
                } catch (error) {
                    console.log(error);
                }
            }
        };
        fetchAllocatedReports();
    }, [tableRowData, account]);

    // Transform data for reports allocation
    function transformData(data) {
        data.forEach((item) => {
            if (item.children && item.children.length > 0) {
                const allChildrenChecked = item.children.every((child) => child.checked);
                item.checked = allChildrenChecked;
                transformData(item.children);
            }
        });
        return data;
    }

    // Handle checkbox change
    const handleCheckboxChange = (itemId, itemType, isChecked) => {
        if (updatedReportData) {
            const updatedCheckedItems = [...updatedReportData];
            if (itemType === "child") {
                updatedCheckedItems.forEach((item) => {
                    item.children?.forEach((child) => {
                        if (child.id === itemId) {
                            child.checked = isChecked;
                            const allChildChecked = item.children.every((ele) => ele.checked);
                            item.checked = allChildChecked;
                        }
                    });
                });
            }
            if (itemType === "item") {
                updatedCheckedItems.forEach((item) => {
                    if (item.id === itemId) {
                        item.checked = isChecked;
                        item.children?.forEach((child) => {
                            child.checked = isChecked;
                            child.children?.forEach((grandchild) => (grandchild.checked = isChecked));
                        });
                    }
                });
            }
            setUpdatedReportData(updatedCheckedItems);
        }
    };

    // Function to get checked items
    function getCheckedItems(data) {
        const checkedItems = [];

        function traverse(node) {
            if (node.checked === true && node.type !== "collapse") {
                checkedItems.push({
                    reportId: node.id,
                });
            }
            node.children?.forEach(traverse);
        }

        data.forEach(traverse);

        return checkedItems;
    }

    // Assign reports
    const assignReports = async () => {
        console.log("Calling assignReports"); // ✅ Add this here
        console.log("updatedReportData before getting checked items:", updatedReportData); // ✅ Also helpful
        const checkedIds = getCheckedItems(updatedReportData);
        console.log("Checked IDs:", checkedIds); // ✅ To verify what’s selected
        const key = account.role === 'Owner' ? 'orgId' : 'userId';
        const reportData = {
            [key]: tableRowData?.id,
            reportId: checkedIds
        };
        try {
            const res = await axiosInstance.post(configData.API_SERVER + `/user/allocate-report`, reportData);
            console.log("Final Payload to send:", reportData);
            toggle();
        } catch (error) {
            console.log(error);
        }
    };

    const TableData = apiData;

    return (
        <React.Fragment>
            <MainCard style={{ color: ' ' }} title="Reports Allocation" secondary={<CardSecondaryAction />}>
                <Grid spacing={gridSpacing}>
                    <Grid item lg={12} sm={12} md={12} className="al_sl__main_grid1">
                        <div style={{ width: '100%', paddingBottom: '25px' }}>
                            <MaterialTable
                                style={{ width: '99.9%' }}
                                actions={actions}
                                isLoading={loading}
                                title={'Reports Data'}
                                columns={account.role === 'Owner' ? columns : memberCols}
                                data={TableData}
                                options={{
                                    pageSize: 10,
                                    actionsColumnIndex: -1,
                                    search: false,
                                    grouping: false,
                                    paging: true,
                                    showFirstLastPageButtons: false,
                                    filtering: false,
                                    headerStyle: {
                                        backgroundColor: 'rgba(173, 216, 230, 0.281)',
                                        color: 'black',
                                        width: '90vw',
                                        height: '45px',
                                        textAlign: 'left',
                                        fontSize: '13px'
                                    },
                                    cellStyle: {
                                        textAlign: 'left',
                                        color: 'black',
                                        height: '40px',
                                        fontSize: '13px'
                                    }
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </MainCard>

            <Modal isOpen={modal} toggle={toggle} backdrop={true} centered={true} size="lg">
                <ModalHeader toggle={toggle}>Assign Reports</ModalHeader>
                <ModalBody style={{ height: '65vh' }}>
                    {tableRowData?.name ? <p>Org Name : {tableRowData.name}</p> : <p>User Name : {tableRowData?.memberName}</p>}
                    <Grid container spacing={2} style={{ height: '50vh', overflowX: 'auto' }}>
                        {updatedReportData &&
                            updatedReportData.map((item, index) => (
                                <Grid item xs={4} key={index}>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', alignItems: 'start' }}>
                                        <div style={{ display: 'flex' }}>
                                            <Checkbox
                                                style={{ marginTop: '-8px' }}
                                                checked={!!item.checked}
                                                onChange={(event) => handleCheckboxChange(item.id, "item", event.target.checked)}
                                            />
                                            <p style={{ margin: '0', fontWeight: '500' }}>{item.title}</p>
                                        </div>
                                        <ul style={{ listStyleType: 'none' }}>
                                            {item.children?.map((ele, index) => (
                                                <div key={index} style={{ display: 'flex' }}>
                                                    <Checkbox
                                                        style={{ marginTop: '-8px' }}
                                                        checked={ele.checked}
                                                        onChange={(event) => handleCheckboxChange(ele.id, "child", event.target.checked)}
                                                    />
                                                    <li>
                                                        <p style={{ fontSize: '15px' }}>{ele.title}</p>
                                                    </li>
                                                </div>
                                            ))}
                                        </ul>
                                    </div>
                                </Grid>
                            ))}
                    </Grid>
                    <Grid container spacing={2} justifyContent="flex-end">
                        <Grid item>
                            <Button
                                color="success"
                                onClick={() => {
                                    console.log("Assign button clicked"); // ✅ Add this to confirm
                                    assignReports(); // ✅ Trigger this immediately
                                    setTableRowData();
                                    setParentChildPairs([]);
                                    setCheckedItems({});
                                }}
                            >
                                Assign
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                color="danger"
                                onClick={() => {
                                    setTableRowData();
                                    setParentChildPairs([]);
                                    setCheckedItems({});
                                    toggle();
                                }}
                            >
                                Close
                            </Button>
                        </Grid>
                    </Grid>
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default ReportAllocation;
